import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { BehaviorSubject, Observable, tap } from 'rxjs';
import { environment } from 'src/environments/environment';

export interface ApiResponse<T = any> {
  status: string;
  message?: string;
  data?: T;
  [key: string]: any;
}

export interface TournamentCategoryPayload {
  id?:            number | string;
  birth_year:     number;
  birth_year_end?: number | null;
  name?:          string;
  pitch_type:     string;
  entry_fee:      number;
  court_name:     string;
  courts?: Array<{ court_name: string; time_from?: string; time_to?: string }>;
}

export interface StoreTournamentPayload {
  id?: number;
  name: string;
  competition_system: string;
  start_date?: string;
  end_date?: string;
  match_duration?: number;
  break_duration?: number;
  max_extra_players?: number;
  extra_player_fee?: number;
  referee_fee?: number;
  prizes_and_terms?: string;
  selected_courts?: any[];
  selected_referees?: any[];
  selected_technical_coaches?: any[];
  categories?: TournamentCategoryPayload[];
}

export interface StoreAcademyPayload {
  tournament_category_id?: number;
  birth_year?: number;
  academy_name: string;
  branch_name?: string;
  team_identifier?: string;
  fee_paid?: number;
  players_count?: number;
  extra_players_count?: number;
  players?: Array<{
    name: string;
    number?: number;
    position?: string;
    birth_date?: string;
    is_extra?: boolean;
  }>;
}

export interface StorePlayerPayload {
  name: string;
  number: number;
  position?: string;
  birth_date?: string;
  is_extra?: boolean;
  is_captain?: boolean;
}

export interface GenerateDrawPayload {
  category_id?: number;
  birth_year?: number;
  number_of_groups?: number;
}

export interface GenerateSchedulePayload {
  category_id?: number;
  birth_year?: number;
  first_round_date: string;
  start_time: string;
  end_time?: string;
  match_days: string[];
  courts: string[];
  exceptions?: Array<{
    date: string;
    from: string;
    to: string;
    reason?: string;
  }>;
  referee_ids?: number[];
  coach_ids?: number[];
}

export interface StoreMatchScorePayload {
  score_a: number;
  score_b: number;
  has_penalty?: boolean;
  penalty_a?: number;
  penalty_b?: number;
  motm_player_name?: string;
  motm_team?: string;
  scorers?: Array<{
    player_name: string;
    team: string;
    goals_count: number;
    assists_count?: number;
  }>;
  cards?: Array<{
    player_name: string;
    team: string;
    card_type: 'yellow' | 'red';
  }>;
}

export interface StoreDisputePayload {
  match_label: string;
  report_text: string;
}

@Injectable({
  providedIn: 'root'
})
export class TournamentService {
  private http = inject(HttpClient);
  private apiUrl = `${environment.baseUrl}`;

  private activeTournamentIdSubject = new BehaviorSubject<number | null>(
    Number(localStorage.getItem('active_tournament_id')) || null
  );
  public activeTournamentId$ = this.activeTournamentIdSubject.asObservable();

  private activeTournamentSubject = new BehaviorSubject<any>(null);
  public activeTournament$ = this.activeTournamentSubject.asObservable();

  setActiveTournamentId(id: number | null): void {
    if (id) {
      localStorage.setItem('active_tournament_id', String(id));
    } else {
      localStorage.removeItem('active_tournament_id');
    }
    this.activeTournamentIdSubject.next(id);
  }

  getActiveTournamentId(): number | null {
    return this.activeTournamentIdSubject.value;
  }

  setActiveTournament(tournament: any): void {
    this.activeTournamentSubject.next(tournament);
    if (tournament?.id) {
      this.setActiveTournamentId(tournament.id);
    }
  }

  // ── Authentication for Tournament Organizer ─────────────
  registerOrganizer(data: any): Observable<any> {
    return this.http.post(`${this.apiUrl}auth/tournament-organizer/register`, data);
  }

  loginOrganizer(data: any): Observable<any> {
    return this.http.post(`${this.apiUrl}auth/tournament-organizer/login`, data);
  }

  getOrganizerProfile(): Observable<any> {
    return this.http.get(`${this.apiUrl}auth/tournament-organizer/me`);
  }

  logoutOrganizer(): Observable<any> {
    return this.http.post(`${this.apiUrl}auth/tournament-organizer/logout`, {});
  }

  // ── Tournament Management ────────────────────────────────
  getTournaments(): Observable<any> {
    return this.http.get(`${this.apiUrl}tournaments`).pipe(
      tap((res: any) => {
        if (res?.data && Array.isArray(res.data) && res.data.length > 0) {
          if (!this.getActiveTournamentId()) {
            this.setActiveTournament(res.data[0]);
          }
        }
      })
    );
  }

  getTournament(id: number | string): Observable<any> {
    return this.http.get(`${this.apiUrl}tournaments/${id}`).pipe(
      tap((res: any) => {
        if (res?.data) {
          this.setActiveTournament(res.data);
        }
      })
    );
  }

  createTournament(payload: StoreTournamentPayload): Observable<any> {
    return this.http.post(`${this.apiUrl}tournaments`, payload).pipe(
      tap((res: any) => {
        if (res?.data) {
          this.setActiveTournament(res.data);
        }
      })
    );
  }

  updateTournament(id: number | string, payload: Partial<StoreTournamentPayload>): Observable<any> {
    return this.http.put(`${this.apiUrl}tournaments/${id}`, payload).pipe(
      tap((res: any) => {
        if (res?.data) {
          this.setActiveTournament(res.data);
        }
      })
    );
  }

  deleteTournament(id: number | string): Observable<any> {
    return this.http.delete(`${this.apiUrl}tournaments/${id}`).pipe(
      tap(() => {
        if (this.getActiveTournamentId() === Number(id)) {
          this.setActiveTournamentId(null);
          this.activeTournamentSubject.next(null);
        }
      })
    );
  }

  closeTournament(id: number | string): Observable<ApiResponse<any>> {
    return this.http.post<ApiResponse<any>>(`${this.apiUrl}tournaments/${id}/close`, {});
  }

  toggleStatus(id: number | string): Observable<ApiResponse<any>> {
    return this.http.patch<ApiResponse<any>>(`${this.apiUrl}tournaments/${id}/toggle-status`, {});
  }

  updateTournamentStatus(id: number | string, status: string): Observable<ApiResponse<any>> {
    return this.http.patch<ApiResponse<any>>(`${this.apiUrl}tournaments/${id}/status`, { status });
  }

  // ── Categories ──────────────────────────────────────────
  addCategory(tournamentId: number | string, payload: TournamentCategoryPayload): Observable<any> {
    return this.http.post(`${this.apiUrl}tournaments/${tournamentId}/categories`, payload);
  }

  deleteCategory(tournamentId: number | string, categoryId: number | string): Observable<any> {
    return this.http.delete(`${this.apiUrl}tournaments/${tournamentId}/categories/${categoryId}`);
  }

  // ── Participating Academies & Squads ───────────────────
  getParticipatingAcademies(tournamentId: number | string, params?: { category_id?: number; birth_year?: number }): Observable<any> {
    let httpParams = new HttpParams();
    if (params?.category_id) httpParams = httpParams.set('category_id', params.category_id);
    if (params?.birth_year) httpParams = httpParams.set('birth_year', params.birth_year);
    return this.http.get(`${this.apiUrl}tournaments/${tournamentId}/academies`, { params: httpParams });
  }

  addParticipatingAcademy(tournamentId: number | string, payload: StoreAcademyPayload): Observable<any> {
    return this.http.post(`${this.apiUrl}tournaments/${tournamentId}/academies`, payload);
  }

  deleteParticipatingAcademy(tournamentId: number | string, academyId: number | string): Observable<any> {
    return this.http.delete(`${this.apiUrl}tournaments/${tournamentId}/academies/${academyId}`);
  }

  addPlayerToSquad(tournamentId: number | string, academyId: number | string, payload: StorePlayerPayload): Observable<any> {
    return this.http.post(`${this.apiUrl}tournaments/${tournamentId}/academies/${academyId}/players`, payload);
  }

  updatePlayerInSquad(tournamentId: number | string, academyId: number | string, playerId: number | string, payload: Partial<StorePlayerPayload> | any): Observable<any> {
    return this.http.put(`${this.apiUrl}tournaments/${tournamentId}/academies/${academyId}/players/${playerId}`, payload);
  }

  removePlayerFromSquad(tournamentId: number | string, academyId: number | string, playerId: number | string): Observable<any> {
    return this.http.delete(`${this.apiUrl}tournaments/${tournamentId}/academies/${academyId}/players/${playerId}`);
  }

  getMyAcademyTeams(params?: { academy_id?: number | string; phone?: string; academy_name?: string }): Observable<any> {
    let httpParams = new HttpParams();
    if (params?.academy_id) httpParams = httpParams.set('academy_id', params.academy_id);
    if (params?.phone) httpParams = httpParams.set('phone', params.phone);
    if (params?.academy_name) httpParams = httpParams.set('academy_name', params.academy_name);
    return this.http.get(`${this.apiUrl}tournaments/my-participating-teams`, { params: httpParams });
  }

  enrollInCategory(tournamentId: number | string, payload: {
    tournament_category_id: number;
    academy_name: string;
    contact_name: string;
    phone: string;
    payment_method: 'cash' | 'online';
    payment_gateway_provider?: string;
    payment_type?: string;
    receipt?: string | null;
  }): Observable<any> {
    return this.http.post(`${this.apiUrl}tournaments/${tournamentId}/enroll`, payload);
  }

  confirmOnlinePayment(bookingCode: string): Observable<any> {
    return this.http.post(`${this.apiUrl}tournaments/reservations/confirm-online-payment`, {
      booking_code: bookingCode
    });
  }


  // ── Draw & Groups ───────────────────────────────────────
  generateDraw(tournamentId: number | string, payload: GenerateDrawPayload): Observable<any> {
    return this.http.post(`${this.apiUrl}tournaments/${tournamentId}/draw/generate`, payload);
  }

  resetDraw(tournamentId: number | string, payload?: { category_id?: number }): Observable<any> {
    return this.http.post(`${this.apiUrl}tournaments/${tournamentId}/draw/reset`, payload || {});
  }

  // ── Smart Scheduling ────────────────────────────────────
  generateSchedule(tournamentId: number | string, payload: GenerateSchedulePayload): Observable<any> {
    return this.http.post(`${this.apiUrl}tournaments/${tournamentId}/schedule/generate`, payload);
  }

  updateMatch(matchId: number | string, payload: any): Observable<any> {
    return this.http.put(`${this.apiUrl}tournaments/matches/${matchId}`, payload);
  }

  swapMatches(tournamentId: number | string, matchAId: number | string, matchBId: number | string): Observable<any> {
    return this.http.post(`${this.apiUrl}tournaments/${tournamentId}/matches/swap`, {
      match_a_id: matchAId,
      match_b_id: matchBId,
    });
  }

  swapTeams(
    tournamentId: number | string,
    payload: {
      match_a_id: number | string;
      team_a_side: 'A' | 'B';
      match_b_id: number | string;
      team_b_side: 'A' | 'B';
    }
  ): Observable<any> {
    return this.http.post(`${this.apiUrl}tournaments/${tournamentId}/matches/swap-teams`, payload);
  }

  publishMatches(tournamentId: number | string, payload: any): Observable<any> {
    return this.http.post(`${this.apiUrl}tournaments/${tournamentId}/matches/publish`, payload);
  }

  toggleMatchPublish(matchId: number | string): Observable<any> {
    return this.http.patch(`${this.apiUrl}tournaments/matches/${matchId}/toggle-publish`, {});
  }

  // ── Scores, Events & Disputes ───────────────────────────
  storeMatchScore(matchId: number | string, payload: StoreMatchScorePayload): Observable<any> {
    return this.http.post(`${this.apiUrl}tournaments/matches/${matchId}/score`, payload);
  }

  submitDispute(matchId: number | string, payload: StoreDisputePayload): Observable<any> {
    return this.http.post(`${this.apiUrl}tournaments/matches/${matchId}/dispute`, payload);
  }

  getDisputes(tournamentId: number | string): Observable<any> {
    return this.http.get(`${this.apiUrl}tournaments/${tournamentId}/disputes`);
  }

  resolveDispute(disputeId: number | string, payload: { status: string; resolution_notes: string }): Observable<any> {
    return this.http.patch(`${this.apiUrl}tournaments/disputes/${disputeId}/resolve`, payload);
  }

  // ── Standings & Top Stats ───────────────────────────────
  getStandings(tournamentId: number | string, params?: { category_id?: number; birth_year?: number }): Observable<any> {
    let httpParams = new HttpParams();
    if (params?.category_id) httpParams = httpParams.set('category_id', params.category_id);
    if (params?.birth_year) httpParams = httpParams.set('birth_year', params.birth_year);
    return this.http.get(`${this.apiUrl}tournaments/${tournamentId}/standings`, { params: httpParams });
  }

  getTopStats(tournamentId: number | string, params?: { category_id?: number; birth_year?: number }): Observable<any> {
    let httpParams = new HttpParams();
    if (params?.category_id) httpParams = httpParams.set('category_id', params.category_id);
    if (params?.birth_year) httpParams = httpParams.set('birth_year', params.birth_year);
    return this.http.get(`${this.apiUrl}tournaments/${tournamentId}/top-stats`, { params: httpParams });
  }

  getTournamentMatches(tournamentId: number | string, params?: { category_id?: number; birth_year?: number; scope?: string; from_date?: string; to_date?: string }): Observable<any> {
    let httpParams = new HttpParams();
    if (params?.category_id) httpParams = httpParams.set('category_id', params.category_id);
    if (params?.birth_year) httpParams = httpParams.set('birth_year', params.birth_year);
    if (params?.scope) httpParams = httpParams.set('scope', params.scope);
    if (params?.from_date) httpParams = httpParams.set('from_date', params.from_date);
    if (params?.to_date) httpParams = httpParams.set('to_date', params.to_date);
    return this.http.get(`${this.apiUrl}tournaments/${tournamentId}/matches`, { params: httpParams });
  }

  updateMatchesVisibility(tournamentId: number | string, payload: { from_date?: string | null; to_date?: string | null; scope?: string } | string): Observable<any> {
    const body = typeof payload === 'string' ? { scope: payload } : payload;
    return this.http.patch(`${this.apiUrl}tournaments/${tournamentId}/matches-visibility`, body);
  }

  // ── Referees (Sports Experts) ───────────────────────────
  enrollReferee(tournamentId: number | string, payload: any): Observable<any> {
    return this.http.post(`${this.apiUrl}tournaments/${tournamentId}/referees/enroll`, payload);
  }

  getTournamentReferees(tournamentId: number | string): Observable<any> {
    return this.http.get(`${this.apiUrl}tournaments/${tournamentId}/referees`);
  }

  getMyOfficiatedMatches(phone?: string): Observable<any> {
    let httpParams = new HttpParams();
    if (phone) httpParams = httpParams.set('phone', phone);
    return this.http.get(`${this.apiUrl}tournaments/referee/my-matches`, { params: httpParams });
  }

  submitRefereeMatchResult(matchId: number | string, payload: any): Observable<any> {
    return this.http.post(`${this.apiUrl}tournaments/matches/${matchId}/referee-result`, payload);
  }

  getPlatformOptions(): Observable<any> {
    return this.http.get(`${this.apiUrl}tournaments/platform-options`);
  }

  searchPlatformPlayers(query: string = ''): Observable<any> {
    let params = new HttpParams();
    if (query) {
      params = params.set('query', query);
    }
    return this.http.get(`${this.apiUrl}tournaments/search-platform-players`, { params });
  }

  checkPlayerByPhone(phone: string): Observable<any> {
    const params = new HttpParams().set('phone', phone);
    return this.http.get(`${this.apiUrl}tournaments/check-player-by-phone`, { params });
  }

  registerPlayerByPhone(phone: string, name: string = ''): Observable<any> {
    return this.http.post(`${this.apiUrl}tournaments/register-player-by-phone`, { phone, name });
  }

  getTournamentBlueprint(tournamentId: number | string): Observable<any> {
    return this.http.get(`${this.apiUrl}tournaments/${tournamentId}/blueprint`);
  }

  saveTournamentBlueprint(tournamentId: number | string, payload: any): Observable<any> {
    return this.http.post(`${this.apiUrl}tournaments/${tournamentId}/blueprint`, payload);
  }
}
